import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  X,
  ShoppingBag,
  Pencil,
  Trash2,
  Check,
  Crosshair,
  Sprout,
  LandPlot,
  Shield,
  Compass,
  Maximize2,
} from 'lucide-react';
import type { Field, Product, Estate } from '../../types';
import {
  fieldAreaHa,
  getAreaTypeDefinition,
  isNatureOrZoneField,
  isZoneField,
  DEFAULT_PRODUCTS,
  isKernelUnit,
  formatPackWord,
} from '../../lib/domain';
import {
  isJagtPoiField,
  parseJagtPoi,
  stripJagtPoiPrefix,
  JAGT_POI_CONFIGS,
  getFieldCentroid,
} from '../../lib/poi';
import { FieldSoilWeatherCard } from '../map/FieldSoilWeatherCard';
import { useDialogA11y } from '../../lib/useDialogA11y';
import {
  SNAP_LABEL,
  nextSnap,
  resolveSnap,
  snapHeights,
  type SheetSnap,
} from '../../lib/sheet-snap';

export interface MobileInspectorSheetProps {
  field: Field | null;
  estate: Estate;
  products: Product[];
  planSelections: Record<string, string>;
  onSelectProduct: (fieldId: string, productId: string) => void;
  onUpdateField: (fieldId: string, updates: Partial<Field>) => void;
  onDeleteField: (fieldId: string) => void;
  onClose: () => void;
  onOpenCart: () => void;
  onStartDrawing?: (kind?: 'vildtager' | 'zone' | 'line') => void;
  onEditShape?: (fieldId: string) => void;
  isSemiPro?: boolean;
  isProView?: boolean;
  notify?: (message: string) => void;
}

export const MobileInspectorSheet: React.FC<MobileInspectorSheetProps> = ({
  field,
  estate,
  products,
  planSelections,
  onSelectProduct,
  onUpdateField,
  onDeleteField,
  onClose,
  onOpenCart,
  onStartDrawing,
  onEditShape,
  isSemiPro,
  isProView,
  notify,
}) => {
  const { ref: sheetRef } = useDialogA11y({ open: Boolean(field), onClose });

  // Bottom sheet med tre snap-punkter (lav/halv/fuld). Lav slipper kortet fri til panorering.
  const [snap, setSnap] = useState<SheetSnap>('half');
  const [dragHeight, setDragHeight] = useState<number | null>(null);
  const [viewportHeight, setViewportHeight] = useState(() =>
    typeof window === 'undefined' ? 800 : (window.visualViewport?.height ?? window.innerHeight),
  );
  const drag = useRef<{
    startY: number;
    startTime: number;
    lastY: number;
    lastTime: number;
  } | null>(null);
  useEffect(() => {
    const update = () => setViewportHeight(window.visualViewport?.height ?? window.innerHeight);
    window.addEventListener('resize', update);
    window.visualViewport?.addEventListener('resize', update);
    return () => {
      window.removeEventListener('resize', update);
      window.visualViewport?.removeEventListener('resize', update);
    };
  }, []);
  const heights = snapHeights(viewportHeight);

  const onHandlePointerDown = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    if ((event.target as HTMLElement).closest('button:not(.mobile-sheet-handle-btn)')) return;
    const now = performance.now();
    drag.current = { startY: event.clientY, startTime: now, lastY: event.clientY, lastTime: now };
  }, []);
  const onHandlePointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    const d = drag.current;
    if (!d) return;
    d.lastY = event.clientY;
    d.lastTime = performance.now();
    // Fang først pointeren ved et reelt træk, så et tryk stadig rammer håndtags-knappen (click).
    if (Math.abs(event.clientY - d.startY) < 6 && dragHeight === null) return;
    if (!event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.setPointerCapture(event.pointerId);
    }
    const next = heights[snap] - (event.clientY - d.startY);
    setDragHeight(Math.max(heights.peek, Math.min(heights.full, next)));
  };
  const finishDrag = (event: React.PointerEvent<HTMLDivElement>) => {
    const d = drag.current;
    drag.current = null;
    if (!d) return;
    const dy = event.clientY - d.startY;
    const dt = Math.max(1, d.lastTime - d.startTime);
    setDragHeight(null);
    if (Math.abs(dy) < 6) return; // tryk: håndteres af onClick på håndtaget
    setSnap(resolveSnap(snap, heights, dy, (d.lastY - d.startY) / dt));
  };

  if (!field) return null;

  const isPoi = isJagtPoiField(field);
  const poiData = isPoi ? parseJagtPoi(field.notes) : null;
  const isZone = isZoneField(field);
  const isNature = isNatureOrZoneField(field);

  const areaHa = fieldAreaHa(field.geometry);
  const areaM2 = Math.round(areaHa * 10000);
  const areaDef = getAreaTypeDefinition(field.areaType, field.kind);

  const currentSelectionId = planSelections[field.id] || '';

  // Top 3 Recommended Blends
  const hunterscorn =
    products.find((p) => p.id === 'grass') || DEFAULT_PRODUCTS.find((p) => p.id === 'grass');
  const deerBlend =
    products.find((p) => p.id === 'deer') || DEFAULT_PRODUCTS.find((p) => p.id === 'deer');
  const boostBlend =
    products.find((p) => p.id === 'flower') || DEFAULT_PRODUCTS.find((p) => p.id === 'flower');

  const topThreeIds = ['grass', 'deer', 'flower'];
  const otherProducts = products.filter((p) => !topThreeIds.includes(p.id));
  const [showAllBlends, setShowAllBlends] = useState(() =>
    otherProducts.some((p) => p.id === currentSelectionId),
  );

  // Strict domain math:
  // Hunters Corn: 100.000 kerner/ha, 25.000 kerner/pose
  const cornPacks = Math.ceil((areaHa * 100000) / 25000) || 1;
  // Deer Blend: 12 kg/ha, 5 kg/pakke
  const deerPacks = Math.ceil((areaHa * 12) / 5) || 1;
  // Boost Blend: 6.5 kg/ha, 5 kg/pakke
  const boostPacks = Math.ceil((areaHa * 6.5) / 5) || 1;

  const handleSelectSeed = (productId: string) => {
    onSelectProduct(field.id, productId);
    notify?.('Frøblanding valgt.');
  };

  const handleAddToCartAndJump = () => {
    // If no product selected, default to Hunters Corn for vildtager
    const chosenId = currentSelectionId || 'grass';
    if (!currentSelectionId) {
      onSelectProduct(field.id, chosenId);
    }
    notify?.('Lagt i indkøbskurven!');
    onOpenCart();
  };

  return (
    <>
      <div
        className={`mobile-inspector-backdrop${snap === 'peek' ? ' is-peek' : ''}`}
        onClick={onClose}
        aria-hidden="true"
      />
      <div
        ref={sheetRef}
        className={`mobile-inspector-sheet field-inspector is-snap-${snap}${dragHeight !== null ? ' is-dragging' : ''}`}
        style={{ ['--sheet-max' as string]: `${dragHeight ?? heights[snap]}px` }}
        role="dialog"
        aria-modal="true"
        aria-label="Mark-inspektør"
      >
        <div
          className="mobile-sheet-header"
          onPointerDown={onHandlePointerDown}
          onPointerMove={onHandlePointerMove}
          onPointerUp={finishDrag}
          onPointerCancel={finishDrag}
        >
          <button
            type="button"
            className="mobile-sheet-handle-btn"
            aria-label={`Skift højde på panelet (nu ${SNAP_LABEL[snap]})`}
            onClick={() => setSnap((current) => nextSnap(current))}
            onKeyDown={(e) => {
              if (e.key === 'ArrowUp') {
                e.preventDefault();
                setSnap((c) => (c === 'peek' ? 'half' : 'full'));
              } else if (e.key === 'ArrowDown') {
                e.preventDefault();
                setSnap((c) => (c === 'full' ? 'half' : 'peek'));
              }
            }}
          >
            <span className="mobile-sheet-drag-handle sheet-handle" aria-hidden="true" />
          </button>
          <div className="mobile-sheet-title-row">
            <div className="mobile-sheet-title-info">
              <span className="mobile-sheet-type-pill">
                {isPoi ? 'JAGT-PUNKT' : isZone ? 'REVIROMRÅDE' : areaDef.label}
              </span>
              <span style={{ fontSize: '0.82rem', color: '#94a3b8' }}>{estate.name}</span>
            </div>
            <button
              type="button"
              className="mobile-sheet-close-btn field-inspector-close btn-close"
              onClick={onClose}
              aria-label="Luk mark-inspektør"
              title="Luk"
            >
              <X size={18} />
            </button>
          </div>
        </div>

        <div className="mobile-sheet-scrollable">
          {/* STEP 1: Navn & Areal */}
          <div className="mobile-step-container">
            <div className="mobile-step-heading">
              <span className="mobile-step-num">1</span>
              <span>{isPoi ? 'Punkt & Placering' : 'Marknavn & Areal'}</span>
            </div>

            <input
              type="text"
              className="mobile-field-name-input form-input"
              value={field.name}
              onChange={(e) => onUpdateField(field.id, { name: e.target.value })}
              placeholder={isPoi ? 'Navn på jagtpunkt...' : 'Markens navn...'}
              aria-label="Markens navn"
            />

            {!isPoi && (
              <div className="mobile-area-badges-row">
                <div className="mobile-area-badge">
                  <strong>
                    {areaHa.toLocaleString('da-DK', {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </strong>
                  <small>hektar (ha)</small>
                </div>
                <div
                  className="mobile-area-badge"
                  style={{
                    background: 'rgba(255,255,255,0.06)',
                    borderColor: 'rgba(255,255,255,0.12)',
                  }}
                >
                  <strong style={{ color: '#e2e8f0' }}>{areaM2.toLocaleString('da-DK')}</strong>
                  <small>m²</small>
                </div>
              </div>
            )}

            {isPoi && poiData && (
              <div
                style={{
                  fontSize: '0.82rem',
                  color: '#cbd5e1',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 4,
                }}
              >
                <div>
                  <strong>Type:</strong> {JAGT_POI_CONFIGS[poiData.type]?.label || 'Punkt'}
                </div>
                {poiData.direction && (
                  <div>
                    <strong>Skudretning:</strong> {poiData.direction}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* STEP 2: Frøvalg touch-cards (kun for dyrkede marker / vildtagre) */}
          {!isPoi && !isNature && (
            <div className="mobile-step-container">
              <div className="mobile-step-heading">
                <span className="mobile-step-num">2</span>
                <span>Vælg Frøblanding</span>
              </div>

              <div className="mobile-seed-cards-grid">
                {/* 1. Hunters Corn */}
                {hunterscorn && (
                  <button
                    type="button"
                    className={`mobile-seed-card ${currentSelectionId === 'grass' ? 'selected' : ''}`}
                    onClick={() => handleSelectSeed('grass')}
                  >
                    <div className="mobile-seed-info">
                      <div
                        className="mobile-seed-name"
                        style={{ display: 'flex', alignItems: 'center', gap: 6 }}
                      >
                        <Sprout size={16} color="#86efac" />
                        <span>Hunters Corn</span>
                        {currentSelectionId === 'grass' && <Check size={16} color="#4ade80" />}
                      </div>
                      <div className="mobile-seed-sub">
                        100.000 kerner/ha · Suverænt skjul &amp; energi
                      </div>
                    </div>
                    <div className="mobile-seed-calc">
                      <div className="mobile-seed-packs">
                        {cornPacks} {cornPacks === 1 ? 'pose' : 'poser'}
                      </div>
                      <div className="mobile-seed-rate">25.000 k/pose</div>
                    </div>
                  </button>
                )}

                {/* 2. Deer Blend */}
                {deerBlend && (
                  <button
                    type="button"
                    className={`mobile-seed-card ${currentSelectionId === 'deer' ? 'selected' : ''}`}
                    onClick={() => handleSelectSeed('deer')}
                  >
                    <div className="mobile-seed-info">
                      <div
                        className="mobile-seed-name"
                        style={{ display: 'flex', alignItems: 'center', gap: 6 }}
                      >
                        <LandPlot size={16} color="#ca8a04" />
                        <span>Deer Blend</span>
                        {currentSelectionId === 'deer' && <Check size={16} color="#4ade80" />}
                      </div>
                      <div className="mobile-seed-sub">
                        12 kg/ha · Fødemagnet for rå- &amp; kronvildt
                      </div>
                    </div>
                    <div className="mobile-seed-calc">
                      <div className="mobile-seed-packs">
                        {deerPacks} {deerPacks === 1 ? 'pakke' : 'pakker'}
                      </div>
                      <div className="mobile-seed-rate">5 kg/pakke</div>
                    </div>
                  </button>
                )}

                {/* 3. Boost Blend */}
                {boostBlend && (
                  <button
                    type="button"
                    className={`mobile-seed-card ${currentSelectionId === 'flower' ? 'selected' : ''}`}
                    onClick={() => handleSelectSeed('flower')}
                  >
                    <div className="mobile-seed-info">
                      <div
                        className="mobile-seed-name"
                        style={{ display: 'flex', alignItems: 'center', gap: 6 }}
                      >
                        <Sprout size={16} color="#ec4899" />
                        <span>Boost Blend</span>
                        {currentSelectionId === 'flower' && <Check size={16} color="#4ade80" />}
                      </div>
                      <div className="mobile-seed-sub">
                        6,5 kg/ha · Hurtigtvoksende blomster &amp; kål
                      </div>
                    </div>
                    <div className="mobile-seed-calc">
                      <div className="mobile-seed-packs">
                        {boostPacks} {boostPacks === 1 ? 'pakke' : 'pakker'}
                      </div>
                      <div className="mobile-seed-rate">5 kg/pakke</div>
                    </div>
                  </button>
                )}
              </div>

              {/* PW-007 Punkt 3: Flere blandinger vises som tilsvarende valgbare touch-kort uden separat dropdown */}
              {otherProducts.length > 0 && (
                <div style={{ marginTop: 8 }}>
                  <button
                    type="button"
                    onClick={() => setShowAllBlends((prev) => !prev)}
                    className="mobile-sec-btn"
                    style={{
                      width: '100%',
                      justifyContent: 'center',
                      background: 'rgba(255, 255, 255, 0.06)',
                      borderColor: 'rgba(255, 255, 255, 0.14)',
                      color: '#cbd5e1',
                      fontSize: '0.8rem',
                      fontWeight: 600,
                      padding: '8px 12px',
                    }}
                  >
                    <span>
                      {showAllBlends
                        ? 'Færre blandinger ▲'
                        : `Flere blandinger (${otherProducts.length}) ▼`}
                    </span>
                  </button>

                  {showAllBlends && (
                    <div className="mobile-seed-cards-grid" style={{ marginTop: 8 }}>
                      {otherProducts.map((p) => {
                        const isCorn = isKernelUnit(p.unit);
                        const packs = isCorn
                          ? Math.ceil((areaHa * 100000) / 25000) || 1
                          : Math.ceil((areaHa * (p.rateKgHa || 10)) / (p.packKg || 5)) || 1;
                        const packWord = formatPackWord(packs, p.unit);
                        const rateStr = isCorn
                          ? '100.000 kerner/ha'
                          : `${(p.rateKgHa || 10).toString().replace('.', ',')} kg/ha`;
                        const packSizeStr = isCorn ? '25.000 k/pose' : `${p.packKg || 5} kg/pakke`;
                        const isSelected = currentSelectionId === p.id;

                        return (
                          <button
                            key={p.id}
                            type="button"
                            className={`mobile-seed-card ${isSelected ? 'selected' : ''}`}
                            onClick={() => handleSelectSeed(p.id)}
                          >
                            <div className="mobile-seed-info">
                              <div
                                className="mobile-seed-name"
                                style={{ display: 'flex', alignItems: 'center', gap: 6 }}
                              >
                                <Sprout size={16} color={p.color || '#86efac'} />
                                <span>{p.name}</span>
                                {isSelected && <Check size={16} color="#4ade80" />}
                              </div>
                              <div className="mobile-seed-sub">
                                {rateStr} · {p.target || p.purpose || p.description}
                              </div>
                            </div>
                            <div className="mobile-seed-calc">
                              <div className="mobile-seed-packs">
                                {packs} {packWord}
                              </div>
                              <div className="mobile-seed-rate">{packSizeStr}</div>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* STEP 3: Bestilling & Kurv */}
          {!isPoi && !isNature && (
            <div
              className="mobile-step-container mobile-step-order"
              style={{ background: 'transparent', border: 'none', padding: 0 }}
            >
              <button
                type="button"
                className="mobile-cart-action-btn"
                onClick={handleAddToCartAndJump}
                style={{
                  background: 'linear-gradient(135deg, #16a34a, #15803d)',
                  color: '#ffffff',
                  border: '1px solid rgba(74, 222, 128, 0.4)',
                  borderRadius: 12,
                  minHeight: 48,
                  fontSize: '0.92rem',
                  fontWeight: 700,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 8,
                  boxShadow: '0 4px 16px rgba(22, 163, 74, 0.4)',
                  cursor: 'pointer',
                  width: '100%',
                }}
              >
                <ShoppingBag size={18} />
                <span>Føj til kurv &amp; Gå til bestilling</span>
              </button>
            </div>
          )}

          {/* Semi-pro / Pro Seeding Weather Card (ikke for Jagt-POI eller Revirgrænse/Område) */}
          {(isSemiPro || isProView) && !isPoi && !isZone && (
            <div style={{ marginTop: 4 }}>
              <FieldSoilWeatherCard
                centroid={getFieldCentroid(field.geometry)}
                selectedBlendId={currentSelectionId}
                products={products}
              />
            </div>
          )}

          {/* Inspector Draw & Shape Actions */}
          <div className="inspector-draw-actions" style={{ display: 'flex', gap: 8, marginTop: 4 }}>
            {onStartDrawing && (
              <button
                type="button"
                className="mobile-sec-btn"
                onClick={() => {
                  onClose();
                  onStartDrawing('vildtager');
                }}
              >
                <Pencil size={15} />
                <span>Tegn ny mark</span>
              </button>
            )}

            {onEditShape && (
              <button
                type="button"
                className="mobile-sec-btn"
                onClick={() => {
                  onClose();
                  onEditShape(field.id);
                }}
              >
                <Crosshair size={15} />
                <span>Rediger facon</span>
              </button>
            )}

            <button
              type="button"
              className="mobile-sec-btn danger"
              onClick={() => {
                onDeleteField(field.id);
                onClose();
              }}
            >
              <Trash2 size={15} />
              <span>Slet</span>
            </button>
          </div>
        </div>
      </div>
    </>
  );
};
